import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaCoins, FaArrowLeft, FaWallet, FaShieldAlt, FaPlus, FaMinus, FaBolt, FaLock
} from "react-icons/fa";
import { useAppContext } from "../context/AppContext";
import Alert from "./Alert";
import ConfirmInvestmentModal from "./ConfirmInvestmentModal";
import InvoiceModal from "./InvoiceModal";



const PLAN_NAME_MAP = {
  plan1: "Core Growth",
  plan2: "Institutional Blend",
  plan3: "Founder's Syndicate",
};


/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  accentGlow: "rgba(234,179,8,0.3)",
  blue: "#3b82f6",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  danger: "#ef4444",
  success: "#22c55e",
};

/* ── Glowing separator ── */
const GoldSep = () => (
  <div style={{
    height: 1,
    background: `linear-gradient(90deg,transparent,rgba(234,179,8,0.35),transparent)`,
    margin: "20px 0",
  }} />
);

/* ── Animated background particles ── */
function FloatDots() {
  const dots = useMemo(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 4,
      dur: 4 + Math.random() * 4,
      gold: Math.random() > 0.5,
    })), []);

  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
      {dots.map((d) => (
        <motion.div
          key={d.id}
          animate={{ y: [-8, 8, -8], opacity: [0.2, 0.6, 0.2] }}
          transition={{ duration: d.dur, delay: d.delay, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            left: d.left, top: d.top,
            width: d.size, height: d.size,
            borderRadius: "50%",
            background: d.gold ? T.accent : T.blue,
          }}
        />
      ))}
      {/* radial glows */}
      <div style={{
        position: "absolute", top: "10%", right: "5%",
        width: 320, height: 320, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(234,179,8,0.07) 0%,transparent 70%)",
      }} />
      <div style={{
        position: "absolute", bottom: "15%", left: "5%",
        width: 280, height: 280, borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(59,130,246,0.07) 0%,transparent 70%)",
      }} />
    </div>
  );
}

/* ── Stat row ── */
function StatRow({ label, value, valueColor }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "10px 0",
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em" }}>
        {label}
      </span>
      <span style={{ fontSize: 12, fontWeight: 900, color: valueColor || T.textPrimary, fontFamily: "monospace" }}>
        {value}
      </span>
    </div>
  );
}

const InvestmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation()

  const {
    HandleFetchUserData,
    FetchUserData,
    HandleCreateInvestmentPlan,
    alert,
    showAlert,
    PaymentINV,
    fetchLatestInvoice,
    invoice,
    loading,
  } = useAppContext();

  const { name, amount } = location.state || {};
  const baseAmount = Number(amount || 0);

  const [customAmount, setCustomAmount] = useState(baseAmount);
  const [selectedPlan, setSelectedPlan] = useState({
    name: name || "",
    amount: baseAmount,
  });
  const totalSuggested = customAmount ;
  const AfterTaxAMT = customAmount +8;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  useEffect(() => {
    HandleFetchUserData();
    fetchLatestInvoice();
  }, []);

  useEffect(() => {
    const a = Number(amount || 0);
    setSelectedPlan((prev) => ({ ...prev, name: name || prev.name, amount: a }));
    setCustomAmount(a);
  }, [name, amount]);

  const userWalletAddress = FetchUserData?.walletAddress || "";
  const userNetwork = FetchUserData?.network || "";

  const maskedWallet = useMemo(() => {
    if (!userWalletAddress) return "";
    return `${userWalletAddress.slice(0, 6)}...${userWalletAddress.slice(-4)}`;
  }, [userWalletAddress]);

  const handleProceed = async () => {
    const payload = { ...selectedPlan, amount: Number(totalSuggested) };
    const ok = await HandleCreateInvestmentPlan(payload);
    if (ok) {
      await fetchLatestInvoice();
      setConfirmOpen(false);
      setInvoiceOpen(true);
    }
  };

  const getPlanDisplayName = (planKey) => {
    if (!planKey) return "";
    return PLAN_NAME_MAP[planKey.toLowerCase()] || planKey;
  };

  const finalPlan = useMemo(() => ({
    ...selectedPlan,
    amount: Number(totalSuggested),
  }), [selectedPlan, totalSuggested]);

  /* ── No plan selected state ── */
  if (!name || !amount) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", background: T.bg, padding: 24,
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            width: "100%", maxWidth: 360,
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: 28, padding: 40, textAlign: "center",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: T.textPrimary, marginBottom: 8 }}>
            No Plan Selected
          </h2>
          <p style={{ fontSize: 13, color: T.textSecondary, marginBottom: 32, lineHeight: 1.6 }}>
            Please return to the plans page to choose an investment option.
          </p>
          <button
            onClick={() => navigate("/plans")}
            style={{
              width: "100%", padding: "14px 0",
              background: T.accent, color: "#0a0d14",
              fontWeight: 900, fontSize: 12, letterSpacing: "0.15em",
              textTransform: "uppercase", border: "none", borderRadius: 14, cursor: "pointer",
            }}
          >
            Go to Plans
          </button>
        </motion.div>
      </div>
    );
  }

  const invoiceData = PaymentINV || invoice || null;
  const canProceed = userWalletAddress && customAmount >= baseAmount && !loading;

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      color: T.textPrimary,
      fontFamily: "system-ui,-apple-system,sans-serif",
      overflowX: "hidden",
      paddingBottom: 80,
    }}>
      <FloatDots />

      <Alert
        type={alert.type}
        message={alert.message}
        isOpen={alert.isOpen}
        onClose={() => showAlert({ isOpen: false, type: "", message: "" })}
      />
      <ConfirmInvestmentModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onProceed={handleProceed}
        plan={finalPlan}
        loading={loading}
      />
      <InvoiceModal
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        invoice={invoiceData}
      />

      {/* ── Scroll progress bar ── */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, height: 2, zIndex: 50,
        background: `linear-gradient(90deg,${T.accent},${T.blue})`,
      }} />

      <div style={{
        position: "relative", zIndex: 10,
        maxWidth: 460, margin: "0 auto", padding: "0 20px",
      }}>

        {/* ── Back button ── */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{ paddingTop: 28, marginBottom: 28 }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "9px 18px",
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${T.border}`,
              borderRadius: 999,
              color: T.textSecondary, fontSize: 11, fontWeight: 700,
              cursor: "pointer", transition: "all 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.color = T.accent; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSecondary; }}
          >
            <FaArrowLeft size={10} /> Back
          </button>
        </motion.div>

        {/* ══════════ HERO HEADER — comes from TOP ══════════ */}
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.1 }}
          style={{ textAlign: "center", marginBottom: 32 }}
        >
          {/* Icon — spins in */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 18, delay: 0.2 }}
            style={{
              width: 72, height: 72, borderRadius: 22,
              background: `linear-gradient(135deg,${T.accent},#ca8a04)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px",
              boxShadow: `0 0 0 1px rgba(234,179,8,0.3), 0 12px 40px rgba(234,179,8,0.25)`,
              position: "relative",
            }}
          >
            {/* pulse ring */}
            <motion.div
              animate={{ scale: [1, 1.4, 1], opacity: [0.4, 0, 0.4] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              style={{
                position: "absolute", inset: -8, borderRadius: 30,
                border: `1px solid rgba(234,179,8,0.4)`,
              }}
            />
            <FaCoins size={30} color="#0a0d14" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            style={{
              fontSize: "clamp(1.5rem,5vw,2rem)",
              fontWeight: 900, letterSpacing: "-0.04em",
              color: T.textPrimary, margin: "0 0 6px",
              textTransform: "uppercase",
            }}
          >
            {getPlanDisplayName(selectedPlan.name)}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            style={{
              fontSize: 10, fontWeight: 900, color: T.accent,
              letterSpacing: "0.3em", textTransform: "uppercase",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            <span style={{ width: 20, height: 1, background: T.accent, display: "inline-block" }} />
            Review Deposit
            <span style={{ width: 20, height: 1, background: T.accent, display: "inline-block" }} />
          </motion.div>
        </motion.div>

        {/* ══════════ AMOUNT DISPLAY — from LEFT ══════════ */}
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.65, ease: "easeOut", delay: 0.3 }}
          style={{
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: 24,
            padding: "28px 24px",
            marginBottom: 16,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* gold top accent */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 2,
            background: `linear-gradient(90deg,${T.accent},transparent)`,
          }} />

          {/* big price */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <motion.div
              key={customAmount}
              initial={{ scale: 1.08, color: T.accent }}
              animate={{ scale: 1, color: T.textPrimary }}
              transition={{ duration: 0.3 }}
              style={{
                fontSize: "clamp(2.8rem,10vw,3.8rem)",
                fontWeight: 900,
                letterSpacing: "-0.05em",
                lineHeight: 1,
              }}
            >
              <span style={{ color: T.accent, fontSize: "0.55em", verticalAlign: "top", marginTop: "0.45em", display: "inline-block" }}>$</span>
              {customAmount}
              <span style={{ fontSize: "0.35em", color: T.blue, marginLeft: 6, verticalAlign: "bottom" }}>USD</span>
            </motion.div>
            <div style={{ fontSize: 10, color: T.textMuted, marginTop: 6, fontWeight: 700, letterSpacing: "0.1em" }}>
              MIN REQUIRED: <span style={{ color: T.textSecondary }}>${baseAmount}</span>
            </div>
          </div>

          {/* amount controller */}
          <div style={{
            background: "rgba(0,0,0,0.3)",
            border: `1px solid rgba(255,255,255,0.06)`,
            borderRadius: 18,
            padding: "16px 16px 12px",
            marginBottom: 20,
          }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 12,
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em" }}>
                Adjust Amount
              </span>
              <span style={{
                fontSize: 9, fontWeight: 800,
                color: T.accent,
                background: "rgba(234,179,8,0.08)",
                border: "1px solid rgba(234,179,8,0.2)",
                padding: "3px 10px", borderRadius: 6,
                letterSpacing: "0.1em", textTransform: "uppercase",
              }}>
                + Increase
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* minus */}
              <button
                type="button"
                onClick={() => setCustomAmount((p) => Math.max(baseAmount, Number(p || 0) - 10))}
                style={{
                  width: 44, height: 44, borderRadius: 13,
                  background: "rgba(255,255,255,0.05)",
                  border: `1px solid ${T.border}`,
                  color: T.textSecondary, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 15, flexShrink: 0, transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.danger; e.currentTarget.style.color = T.danger; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSecondary; }}
              >
                <FaMinus size={13} />
              </button>

              <input
                type="number"
                value={customAmount}
                onChange={(e) => setCustomAmount(Math.max(0, Number(e.target.value)))}
                style={{
                  flex: 1, minWidth: 0,
                  background: "transparent",
                  border: "none",
                  borderBottom: `2px solid rgba(234,179,8,0.3)`,
                  color: T.textPrimary,
                  fontSize: 22, fontWeight: 900, textAlign: "center",
                  outline: "none", padding: "4px 0",
                  transition: "border-color 0.2s",
                }}
                onFocus={e => e.currentTarget.style.borderBottomColor = T.accent}
                onBlur={e => e.currentTarget.style.borderBottomColor = "rgba(234,179,8,0.3)"}
              />

              {/* plus */}
              <button
                type="button"
                onClick={() => setCustomAmount((p) => Number(p || 0) + 10)}
                style={{
                  width: 44, height: 44, borderRadius: 13,
                  background: T.accent,
                  border: "none",
                  color: "#0a0d14", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 15, flexShrink: 0,
                  boxShadow: `0 4px 16px rgba(234,179,8,0.35)`,
                  transition: "transform 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.08)"}
                onMouseLeave={e => e.currentTarget.style.transform = ""}
              >
                <FaPlus size={13} />
              </button>
            </div>
          </div>

          {/* gas fee notice */}
          <div style={{
            background: "rgba(234,179,8,0.05)",
            border: "1px solid rgba(234,179,8,0.18)",
            borderRadius: 14, padding: "12px 14px",
            display: "flex", gap: 10, alignItems: "flex-start",
          }}>
            <FaBolt style={{ color: T.accent, flexShrink: 0, marginTop: 1 }} size={13} />
            <div style={{ fontSize: 11, color: "rgba(253,230,138,0.7)", lineHeight: 1.55 }}>
              Add <strong style={{ color: T.textPrimary }}>2–3 USDT</strong> for network gas fees.
              Suggested total: <strong style={{ color: T.accent }}>${totalSuggested}</strong>
            </div>
          </div>
        </motion.div>

        {/* ══════════ WALLET INFO — from RIGHT ══════════ */}
        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.65, ease: "easeOut", delay: 0.45 }}
          style={{
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: 24, padding: "22px 24px",
            marginBottom: 16, position: "relative", overflow: "hidden",
          }}
        >
          {/* blue top accent */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 2,
            background: `linear-gradient(90deg,${T.blue},transparent)`,
          }} />

          <div style={{
            fontSize: 10, fontWeight: 900, color: T.textMuted,
            textTransform: "uppercase", letterSpacing: "0.2em", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <FaWallet size={11} style={{ color: T.blue }} /> Wallet Details
          </div>

          <StatRow
            label="Wallet"
            value={maskedWallet || "Not Connected"}
            valueColor={maskedWallet ? T.textPrimary : T.danger}
          />
          <GoldSep />
          <StatRow
            label="Network"
            value={userNetwork || "Not Set"}
            valueColor={userNetwork ? T.blue : T.textMuted}
          />
        </motion.div>

        {/* ══════════ SUMMARY — from BOTTOM (up) ══════════ */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: "easeOut", delay: 0.6 }}
          style={{
            background: `linear-gradient(135deg,rgba(234,179,8,0.07) 0%,rgba(10,22,40,0.95) 60%)`,
            border: `1px solid rgba(234,179,8,0.22)`,
            borderRadius: 24, padding: "22px 24px",
            marginBottom: 28,
          }}
        >
          <div style={{
            fontSize: 10, fontWeight: 900, color: T.accent,
            textTransform: "uppercase", letterSpacing: "0.2em", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <FaShieldAlt size={11} /> Order Summary
          </div>

          <StatRow label="Plan" value={getPlanDisplayName(selectedPlan.name)} valueColor={T.accent} />
          <GoldSep />
          <StatRow label="Deposit" value={`$${customAmount} USD`} />
          <GoldSep />
          <StatRow label="Gas Buffer" value="~$8 USDT" valueColor={T.textSecondary} />
          <GoldSep />
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "12px 0 0",
          }}>
            <span style={{ fontSize: 12, fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", letterSpacing: "0.1em" }}>
              Total Suggested
            </span>
            <motion.span
              key={totalSuggested}
              initial={{ scale: 1.15, color: T.accent }}
              animate={{ scale: 1, color: T.accent }}
              style={{ fontSize: 20, fontWeight: 900, color: T.accent }}
            >
              ${AfterTaxAMT}
            </motion.span>
          </div>
        </motion.div>

        {/* ══════════ CTA BUTTON — bounces up ══════════ */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.75 }}
        >
          <motion.button
            onClick={() => setConfirmOpen(true)}
            disabled={!canProceed}
            whileTap={{ scale: 0.97 }}
            whileHover={canProceed ? { scale: 1.02 } : {}}
            style={{
              width: "100%",
              padding: "18px 0",
              borderRadius: 20,
              background: canProceed
                ? `linear-gradient(135deg,${T.accent} 0%,#ca8a04 100%)`
                : "rgba(255,255,255,0.06)",
              color: canProceed ? "#0a0d14" : T.textMuted,
              fontWeight: 900,
              fontSize: 13,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              border: canProceed ? "none" : `1px solid ${T.border}`,
              cursor: canProceed ? "pointer" : "not-allowed",
              boxShadow: canProceed ? `0 8px 32px rgba(234,179,8,0.35), 0 2px 0 rgba(255,255,255,0.1) inset` : "none",
              transition: "all 0.25s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            }}
          >
            {loading ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  style={{ width: 16, height: 16, border: "2px solid #0a0d14", borderTopColor: "transparent", borderRadius: "50%" }}
                />
                Processing...
              </>
            ) : (
              <>
                <FaLock size={12} />
                Confirm Investment
              </>
            )}
          </motion.button>
        </motion.div>

        {/* secure note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          style={{
            textAlign: "center", marginTop: 20,
            fontSize: 10, color: T.textMuted,
            fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase",
          }}
        >
          🔒 Secure Checkout Process
        </motion.p>
      </div>
    </div>
  );
};

export default InvestmentPage;